/**
 * Create Contact Activity Handler
 * Edit handler for creating new activities for a contact
 */

import type { ToolExecutionContext } from '../../types/index.js';
import type { EditPreview } from '../../types/index.js';
import { EditHandlerBase } from '../base/index.js';
import { uuidField, stringField, enumField, objectField, timestampField } from '../../utils/schema-builder.js';
import { generateConfirmationToken } from '../../utils/confirmation-token.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';

/**
 * Create contact activity handler
 * Validates contact and activity type exist, then generates preview with RLS enforcement
 */
export class CreateContactActivityHandler extends EditHandlerBase<any> {
  id = 'create_contact_activity';

  schema = {
    type: 'object',
    properties: {
      contact_id: uuidField('UUID of the contact'),
      type_id: uuidField('UUID of communication type (email, call, meeting, note, etc.)'),
      title: stringField('Activity title'),
      body: stringField('Activity description or details'),
      direction: enumField('Communication direction (optional)', ['inbound', 'outbound']),
      status: stringField('Activity status (completed, pending, follow_up_needed, scheduled, cancelled)'),
      occurred_at: timestampField('ISO 8601 timestamp when activity occurred (defaults to now)'),
      metadata: objectField('Additional metadata as JSON object'),
    },
    required: ['contact_id', 'type_id', 'title'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<EditPreview> {
    const { contact_id, type_id, title, body, direction, status, occurred_at, metadata } = params;

    if (!contact_id || !type_id || !title) {
      throw new Error('contact_id, type_id, and title are required');
    }

    if (!context?.userToken) {
      throw new Error('User token is required for activity creation');
    }

    try {
      // Dynamically import GraphQL types and documents
      const { GetContactsDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Validate contact exists and user has access
      const contactResult = await client.query({
        query: GetContactsDocument,
        variables: {
          id: contact_id,
        },
        fetchPolicy: 'network-only',
      });

      if (contactResult.error) {
        throw new Error(`GraphQL error: ${contactResult.error.message}`);
      }

      const contactData = contactResult.data as any;
      const contact = contactData?.contactsCollection?.edges?.[0]?.node;
      
      if (!contact) {
        throw new Error(`Contact with ID "${contact_id}" not found or you don't have permission to access it`);
      }

      // In production, you would also validate that type_id exists
      // by querying the activity_types table via GraphQL
      // For now, we'll accept any UUID as a valid type_id

      // Generate activity preview
      const activityPreview = {
        id: `temp-${Date.now()}`,
        contact_id,
        type_id,
        title,
        body: body || '',
        direction: direction || 'outbound',
        status: status || 'completed',
        occurred_at: occurred_at || new Date().toISOString(),
        metadata: metadata || {},
        created_at: new Date().toISOString(),
      };

      // Generate changes (all fields are "added" since this is a new activity)
      const changes: Record<string, any> = {};
      for (const [key, value] of Object.entries(activityPreview)) {
        changes[key] = {
          old: undefined,
          new: value,
          type: 'added',
        };
      }

      // Generate confirmation token
      const token = generateConfirmationToken({
        entityType: 'activity',
        entityId: contact_id,
        changes: {
          contact_id,
          type_id,
          title,
          body,
          direction,
          status,
          occurred_at,
          metadata,
        },
        userId: context?.userId || 'system',
        chatId: context?.chatId || 'system',
        messageId: context?.messageId || 'system',
      });

      return {
        type: 'preview',
        entity: 'activity',
        id: contact_id,
        changes,
        confirmationToken: token,
        preview: activityPreview,
      };
    } catch (error) {
      throw new Error(`Activity creation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
